import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Shield, 
  Globe, 
  BookOpen, 
  Settings, 
  Leaf, 
  Users, 
  Building,
  Target,
  CheckCircle,
  Handshake
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const supportAreas = [
    'Board and executive oversight of AI risk and accountability',
    'Government and regulatory leadership on AI policy and national strategies',
    'Public trust and customer confidence through transparent and ethical AI use',
    'Sustainable innovation aligned with long-term societal and environmental goals'
  ];

  const researchAreas = [
    'AI ethics and human-centred design',
    'AI risk assessment and impact assessment methodologies',
    'Governance of high-risk and safety-critical AI systems',
    'Assurance, auditability, and accountability of AI',
    'Societal, economic, and environmental impacts of AI'
  ];

  const governanceAreas = [
    { title: 'AI ethics and responsible AI principles', icon: Shield },
    { title: 'AI impact assessments and risk classification', icon: Target },
    { title: 'AI safety guardrails and assurance mechanisms', icon: Settings },
    { title: 'Integration with enterprise risk management and audit', icon: Building },
    { title: 'Data governance, privacy, and cybersecurity alignment', icon: Globe },
    { title: 'Organisational governance, accountability, and transparency', icon: Users }
  ];

  const sustainabilityGoals = [
    'Use AI responsibly to advance environmental, social, and economic outcomes',
    'Avoid unintended social, ethical, or environmental harm from AI systems',
    'Align AI initiatives with ESG, CSR, and long-term value creation',
    'Strengthen inclusive and equitable access to AI benefits'
  ];

  const stakeholders = [
    'Boards and senior leadership teams',
    'Policymakers, regulators, and public sector leaders',
    'International and multilateral organisations',
    'Academic and research communities',
    'Industry and technology leaders',
    'Not-for-profit and civil society organisations'
  ];

  const trustBenefits = [
    'Build and maintain public and stakeholder trust',
    'Demonstrate accountability and ethical leadership',
    'Prepare for emerging AI regulation and oversight',
    'Enable innovation while managing risk responsibly'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-white">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1674027444485-cec3da58eef4"
            alt="AI Technology"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/50 via-transparent to-white"></div>
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 sm:px-12 text-center">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight text-black mb-6 animate-in fade-in slide-in-from-bottom-8 duration-1000">
            Centre for
            <br />
            <span className="font-normal">Sustainable AI</span>
          </h1>
          <p className="text-xl sm:text-2xl text-gray-700 max-w-4xl mx-auto mb-12 animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-200 font-medium">
            Global Leadership in Safe, Responsible, and Sustainable Artificial Intelligence
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-300">
            <Link to="/contact">
              <Button size="lg" className="hover:scale-105 transition-transform duration-200">
                Talk to an Expert
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/services">
              <Button
                size="lg"
                variant="outline"
                className="hover:scale-105 transition-all duration-200"
              >
                Explore Our Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              The Centre for Sustainable AI is a global, independent organisation providing leadership at the intersection of AI ethics, governance, policy, risk management, and sustainability. We work with governments, international institutions, industry leaders, academia, and civil society to support the safe and responsible design, deployment, and oversight of artificial intelligence.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              As AI increasingly influences economic systems, public services, national security, social cohesion, and environmental outcomes, trust in AI has become a strategic imperative. The Centre for Sustainable AI exists to help organisations and governments build that trust—through robust governance, accountable systems, and sustainable innovation aligned with human values and public interest.
            </p>
          </div>
        </div>
      </section>

      {/* Trusted Partner Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-black rounded-full flex items-center justify-center">
                  <Handshake className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-3xl sm:text-4xl font-light tracking-tight">
                  A Trusted Partner for Safe and Responsible AI
                </h2>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Safe and responsible AI is not a single policy, technology, or compliance exercise. It is a whole-of-system challenge that requires leadership, institutional capability, and coordination across law, technology, ethics, data, cybersecurity, and organisational governance.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                The Centre for Sustainable AI supports leaders to move beyond fragmented or reactive approaches, enabling coherent, risk-based, and future-ready AI governance. We help decision-makers understand where AI creates value, where it introduces risk, and how both can be governed responsibly.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-medium mb-6">Our work supports:</h3>
              <div className="space-y-4">
                {supportAreas.map((area, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-white rounded-lg shadow-sm">
                    <div className="w-6 h-6 bg-black rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-gray-700">{area}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Global Policy Section */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 bg-black rounded-full flex items-center justify-center">
              <Globe className="w-7 h-7 text-white" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight">
              Global Policy, Standards, and Regulatory Leadership
            </h2>
          </div>
          
          <div className="max-w-4xl mb-12">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              The Centre for Sustainable AI actively engages with global peak bodies and standards organisations, contributing to the development and interpretation of international AI norms and governance frameworks. Our work is informed by collaboration with organisations such as the OECD, the IEEE Standards Association, ISO, and the IET.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              We help translate global principles—such as risk-based governance, proportionality, transparency, and accountability—into implementable policies, standards-aligned frameworks, and operational guidance. Our expertise supports alignment with emerging regulatory regimes and voluntary frameworks across jurisdictions, including those shaped by the European Union, UNESCO, and NIST.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {['OECD', 'IEEE', 'ISO', 'IET', 'UNESCO', 'NIST'].map((org) => (
              <Card key={org} className="hover:shadow-lg transition-all duration-300">
                <CardContent className="p-4 text-center">
                  <p className="text-lg font-light">{org}</p>
                  <p className="text-xs text-gray-500 mt-1">Collaborative Engagement</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Research Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-black rounded-full flex items-center justify-center">
                  <BookOpen className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-3xl sm:text-4xl font-light tracking-tight">
                  Research-Driven, Evidence-Based, and Academically Grounded
                </h2>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our work is underpinned by close collaboration with universities and academic research institutes across disciplines including AI, law, ethics, public policy, cybersecurity, and sustainability. This ensures our guidance is evidence-based, methodologically rigorous, and responsive to emerging research.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-medium mb-6">We contribute to applied research, policy development, and thought leadership across areas such as:</h3>
              <ul className="space-y-3">
                {researchAreas.map((area, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-black rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">{area}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* AI Ethics & Governance Section */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 bg-black rounded-full flex items-center justify-center">
              <Settings className="w-7 h-7 text-white" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight">
              AI Ethics, Governance, and Risk Across the Full Lifecycle
            </h2>
          </div>
          
          <div className="max-w-4xl mb-12">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              The Centre for Sustainable AI addresses AI governance across the entire lifecycle, from design and development through deployment, monitoring, and oversight.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              We recognise that AI governance must be embedded within existing organisational systems, not layered on as an afterthought. Our approach aligns AI oversight with data governance, cybersecurity controls, and enterprise risk frameworks to ensure coherence, resilience, and defensibility.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {governanceAreas.map((area, index) => {
              const IconComponent = area.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-5 h-5" />
                      </div>
                      <p className="text-gray-700 pt-2">{area.title}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sustainability Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-black rounded-full flex items-center justify-center">
                  <Leaf className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-3xl sm:text-4xl font-light tracking-tight">
                  Sustainability and the United Nations Sustainable Development Goals
                </h2>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed">
                Sustainability is a foundational principle of our work. The Centre for Sustainable AI aligns its mission and activities with the United Nations Sustainable Development Goals, recognising AI as both a powerful enabler of sustainable development and a source of systemic risk if left ungoverned.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-medium mb-6">We support organisations and governments to:</h3>
              <div className="space-y-4">
                {sustainabilityGoals.map((goal, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-white rounded-lg shadow-sm">
                    <div className="w-6 h-6 bg-black rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-gray-700">{goal}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Working Across Sectors */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 bg-black rounded-full flex items-center justify-center">
              <Building className="w-7 h-7 text-white" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight">
              Working Across Sectors, Industries, and Societies
            </h2>
          </div>
          
          <div className="max-w-4xl mb-12">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              The Centre for Sustainable AI works across all sectors and industries, including government, infrastructure, health, finance, energy, environment, telecommunications, education, and humanitarian contexts.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed italic">
              Our role is to act as a neutral, trusted partner, supporting collaboration across institutional boundaries and national contexts.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {stakeholders.map((stakeholder, index) => (
              <div key={index} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                <div className="w-2 h-2 bg-black rounded-full flex-shrink-0"></div>
                <p className="text-gray-700 text-sm">{stakeholder}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Building Trust Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-black rounded-full flex items-center justify-center">
                  <Target className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-3xl sm:text-4xl font-light tracking-tight">
                  Building Trust, Strengthening Brand, Enabling the Future
                </h2>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed">
                Trust is the foundation of successful AI adoption. Organisations that invest in safe and responsible AI governance strengthen customer experience, brand reputation, regulatory confidence, and long-term resilience.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-medium mb-6">The Centre for Sustainable AI helps leaders:</h3>
              <div className="space-y-4">
                {trustBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-white rounded-lg shadow-sm">
                    <div className="w-6 h-6 bg-black rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-gray-700">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Shared Journey Section */}
      <section className="py-24 bg-black text-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center">
                <Users className="w-7 h-7 text-black" />
              </div>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-8">
              A Shared Journey Toward Responsible AI
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-8">
              We do not position ourselves as owners of the AI governance agenda, but as contributors and collaborators in a shared global effort. Our purpose is to work alongside governments, international institutions, industry, and civil society to shape a future where AI is safe, accountable, sustainable, and aligned with human values.
            </p>
            <p className="text-xl text-gray-200 leading-relaxed font-medium">
              The Centre for Sustainable AI is a place where leaders come together to design the roadmap for trustworthy AI—for organisations, for societies, and for generations to come.
            </p>
          </div>
        </div>
      </section>

      {/* Trusted Engagements Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-14 h-14 bg-black rounded-full flex items-center justify-center">
                <Handshake className="w-7 h-7 text-white" />
              </div>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-4">
              Trusted Engagements
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Working alongside leading organisations in our commitment to safe, responsible, and sustainable AI
            </p>
          </div>

          {/* Logo Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-8 items-center justify-items-center">
            {[
              { src: '/logos/8.png', alt: 'UN Sustainable Development Goals' },
              { src: '/logos/7.png', alt: 'IFRC - International Federation of Red Cross and Red Crescent Societies' },
              { src: '/logos/3.png', alt: 'IEEE' },
              { src: '/logos/4.png', alt: 'OECD.AI Policy Observatory' },
              { src: '/logos/1.png', alt: 'Australian Government Department of Industry, Science and Resources' },
              { src: '/logos/5.png', alt: 'Harvard Business Review' },
              { src: '/logos/6.png', alt: 'WWF - World Wide Fund for Nature' },
              { src: '/logos/11.png', alt: 'Women in AI' },
              { src: '/logos/10.png', alt: 'Global AI Ethics Institute' },
              { src: '/logos/2.png', alt: 'ACS - Australian Computer Society' },
              { src: '/logos/17.png', alt: 'UNSW Sydney' },
              { src: '/logos/14.png', alt: 'The University of Sydney' },
              { src: '/logos/18.png', alt: 'The University of Melbourne' },
              { src: '/logos/15.png', alt: 'UTS - University of Technology Sydney' },
              { src: '/logos/16.png', alt: 'Western Sydney University' },
              { src: '/logos/13.png', alt: 'Boston University' },
              { src: '/logos/9.png', alt: 'The Teachers Guild of NSW' },
              { src: '/logos/12.png', alt: 'The Fair+ Festival of AI and Robotics' },
              { src: '/logos/19.png', alt: 'META61' },
              { src: '/logos/20.png', alt: 'iFusion' }
            ].map((logo, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl p-6 h-24 w-full flex items-center justify-center shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <img 
                  src={logo.src} 
                  alt={logo.alt}
                  className="max-h-16 max-w-full object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                  title={logo.alt}
                />
              </div>
            ))}
          </div>

          <p className="text-center text-sm text-gray-500 mt-12">
            These represent trusted engagements in our capability space for advancing safe, responsible, and sustainable AI—not formal partnerships.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="bg-gray-50 rounded-2xl p-12 sm:p-16 text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-6">
              Ready to Start the Conversation?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
              Talk to one of our experts today and discover how we can help you navigate AI governance, ethics, and sustainability.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="hover:scale-105 transition-transform duration-200">
                  Get in Touch
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/approach">
                <Button
                  size="lg"
                  variant="outline"
                  className="hover:scale-105 transition-all duration-200"
                >
                  Learn About Our Approach
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
